/**
 * 家长端「编辑打卡任务」弹窗里那句实时预览的**唯一实现**。
 *
 * 为什么要抽成纯函数（而不是留在组件里）：
 *  1. 它能被用例直接喂边界值 —— 「没设目标值时 N 不参与」这条语义**只在浏览器里
 *     点着看**是测不到的，而它恰好是最容易写错的一处（写成 `floor(M ÷ N)` 就会
 *     对「每 2 单位得 1 分 + 没目标」显示成 **0 分**）；
 *  2. 组件里不许再手写一遍算式：`describePointsRule()`（`@fht/shared`）内部调
 *     `calculateCheckinPoints()` —— 与后端入账**同一个函数**，
 *     界面承诺的分数因此不可能与真正加的分漂移。
 */
import { describePointsRule } from '@fht/shared';

/** 预览需要的表单切片（component 传入自己的 form，字段少到可以逐一点名）。 */
export interface HabitPointsPreviewInput {
  pointsUnitSize: string;
  pointsPerUnit: string;
  hasTarget: boolean;
  targetValue: string;
  unit: string;
  limitCap: boolean;
  dailyPointsCap: string;
}

/**
 * N 的兜底是 **1**（不是 0）：输入框空着 / 非数字时，「每 1 单位得 M 分」
 * 正是改动前的语义；兜底成 0 会让算分除零。
 *
 * 这里**不**做合法性判断（那是 `validatePointsInput` 的活，它会给人话提示），
 * 预览只负责"无论输入多脏都显示一句不崩的话"。
 */
export function habitPointsPreview(form: HabitPointsPreviewInput): string {
  const n = Math.max(1, Math.trunc(Number(form.pointsUnitSize)) || 1);
  const m = Number(form.pointsPerUnit) || 0;
  return describePointsRule({
    pointsPerUnit: m,
    unitSize: n,
    /**
     * 没勾「设置目标值」时**不传 targetValue** —— 与算分同一套语义：
     * 没有量化目标就没有"单位"可数，这次完成就是"一次"，N 不参与。
     */
    targetValue: form.hasTarget ? Number(form.targetValue) || null : null,
    unit: (form.unit || '').trim() || null,
    dailyPointsCap: form.limitCap ? Number(form.dailyPointsCap) || null : null,
  });
}

/**
 * 预览文案的**短别名**。
 *
 * `HabitFormModal` 内部一直叫它 `pointsPreview`，这里保留同名导出，
 * 免得组件里出现"换个名字调同一个函数"的多余一层。
 */
export const pointsPreview = habitPointsPreview;
